import { useQuery } from "@apollo/react-hooks";
import { DatesSetArg } from "@fullcalendar/core";
import allLocales from "@fullcalendar/core/locales-all";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  makeStyles,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from "@material-ui/core";
import LinearProgress from "@material-ui/core/LinearProgress";
import AddIcon from "@material-ui/icons/Add";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DeleteIcon from "@material-ui/icons/Delete";
import ErrorIcon from "@material-ui/icons/Error";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import PublishIcon from "@material-ui/icons/Publish";
import WarningIcon from "@material-ui/icons/Warning";
import { Alert, AlertTitle } from "@material-ui/lab";
import { useAuth } from "auth";
import { KeycloakProfile } from "auth";
import axios from "axios";
import Colors from "Constants/Colors";
import CPOConstants from "Constants/CPOConstants";
import Routes from "Constants/Routes";
import TimeConstants from "Constants/TimeConstants";
import ValidationConstants from "Constants/ValidationConstants";
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  getISOWeek,
  startOfMonth,
} from "date-fns";
import { Roles } from "Enums/Roles";
import {
  GET_BLOCK_WITH_MARKERS_BY_ID,
  GetBlockWithMarkersQueryResult,
} from "Graphql/BlockQueries";
import i18n from "i18n";
import MaterialTable from "material-table";
import TableRef from "material-table";
import { CPOFileCalendarEntry } from "Models/CPOFileCalendarEntry";
import { CPOFileJustProcessed } from "Models/CPOFileJustProcessed";
import { CPOFileWithCheck } from "Models/CPOFileWithCheck";
import { CPOMonthData } from "Models/CPOMonthData";
import { CPOMonthDataDate } from "Models/CPOMonthDataDate";
import { CPOMonthDataFile } from "Models/CPOMonthDataFile";
import { CPOMonthFileStats } from "Models/CPOMonthFileStats";
import { RouteParams } from "Models/RouteParams";
import { YearCpoFileNames } from "Models/YearCpoFileNames";
import React, {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { downloadProcessingReport } from "Utils/CPOUploadReportUtils";
import { FileUtils } from "Utils/FileUtils";
import { ExcelDownloadIcon } from "Views/Components/Icons/ExcelDownloadIcon";
import { TableIcons } from "Views/Components/Icons/TableIcons";
import { FileDropZone } from "../FileDropZone/FileDropZone";
import { QuestionMarkIcon } from "../Icons/QuestionMark";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      alignItems: "stretch",
      minHeight: "800px",
    },

    left: {
      flexGrow: 1,
      display: "flex",
      flexDirection: "column",
    },
    table: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      padding: theme.spacing(1),
      height: "100%",
    },
    dropZone: {
      padding: theme.spacing(1),
    },

    middle: {
      padding: theme.spacing(1),
      alignItems: "center",
    },
    calendar: {
      height: "100%",
      padding: theme.spacing(2),
      margin: "auto",
      minHeight: "1000px",
    },

    right: {
      padding: theme.spacing(1),
    },
    rightTable: {
      height: "50%",
      minHeight: "400px",
      marginBottom: theme.spacing(1),
    },
    infoDisplay: {
      padding: theme.spacing(1),
    },
    monthTitle: {
      padding: theme.spacing(1),
      justifyContent: "space-between",
    },
    helpDialog: {
      padding: theme.spacing(1),
      justifyContent: "space-between",
    },
    deferralButton: {
      padding: theme.spacing(1),
    },
    filesListPaper: {
      paddingTop: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    filesList: {
      paddingTop: theme.spacing(1),
      marginTop: theme.spacing(1),
      maxHeight: 300,
      overflowY: "auto",
    },
  }),
);

export const CPOFileSubmission: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { blockId } = useParams<RouteParams>();
  const { t } = useTranslation("cpo");
  const { auth } = useAuth();
  const [userProfile, setUserProfile] = useState<KeycloakProfile | undefined>(
    undefined,
  );

  const [dateRangeSelected, setDateRangeSelected] = useState<DatesSetArg>();
  const [dateSelection, setDateSelection] = useState<{
    month: number | null;
    year: number | null;
  }>({ month: null, year: null });

  const [dataToBeProcessed, setDataToBeProcessed] = useState<
    CPOFileWithCheck | CPOFileWithCheck[]
  >();

  const [yearDateSelection, setYearDateSelection] = useState<{
    year: number | null;
  }>({ year: null });

  const [processedFilesForCalendar, setProcessedFilesForCalendar] = useState<
    CPOFileCalendarEntry[]
  >([]);
  const [selectedDateForCalendar, setSelectedDateForCalendar] = useState<
    CPOFileCalendarEntry[]
  >([]);
  const [failedCheckDatesForCalendar, setFailedCheckDatesForCalendar] =
    useState<CPOFileCalendarEntry[]>([]);

  const [justProcessedFiles, setJustProcessedFiles] = useState<
    CPOFileJustProcessed[]
  >([]);

  const [monthCpoData, setMonthCpoData] = useState<CPOMonthData>();
  const [yearCpoFileNames, setYearCpoFileNames] = useState<YearCpoFileNames>();
  const [filteredMonthCpoData, setFilteredMonthCpoData] =
    useState<CPOMonthData>();
  const [filteredMonthCpoDataBool, setFilteredMonthCpoDataBool] =
    useState(false);
  const [azureUploadRecordMap, setAzureUploadRecordMap] = useState<
    Record<string, boolean>
  >({});
  const [monthSelectedBool, setMonthSelectedBool] = useState(false);
  const [disableSubmitDeleteButtons, setDisableSubmitDeleteButtons] =
    useState(false);
  const [tableItemsSelectable, setTableItemsSelectable] = useState(true);

  const [submitPopUpOpen, setSubmitPopUpOpen] = useState(false);
  const [submitResponsePopUpOpen, setSubmitResponsePopUpOpen] = useState(false);

  const processedDatesTableRef = useRef<TableRef<CPOMonthDataDate>>(null);

  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [cpoSubmissionHelpDialogOpen, setCpoSubmissionHelpDialogOpen] =
    useState(false);
  const [dropzoneDisabledBool, setDropzoneDisabledBool] = useState(false);
  const [uploadLoadingSpinner, setUploadLoadingSpinner] = useState(false);
  const [fetchMonthDataError, setFetchMonthDataError] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [processingSeconds, setProcessingSeconds] = useState(0);
  const [uploadSummary, setUploadSummary] = useState({
    totalFiles: 0,
    uploadTime: 0,
    processingTime: 0,
    totalTime: 0,
    startDateTime: "",
    endDateTime: "",
  });
  const [monthStats, setMonthStats] = useState<CPOMonthFileStats | null>(null);

  const enabledCpoSubmissionBlocks = (() => {
    let rawEnv = process.env.REACT_APP_ENABLED_CPO_UPLOAD_BLOCKS;
    if (!rawEnv) { return []; }
    try {
      if (rawEnv.endsWith(";")) {
        rawEnv = rawEnv.slice(0, -1);
      }

      return JSON.parse(rawEnv);
    } catch (error) {
      console.error(
        "Failed to parse REACT_APP_ENABLED_CPO_UPLOAD_BLOCKS from .env:",
        error,
      );
      return [];
    }
  })();

  const handleDialogOpen = () => {
    setSubmitDialogOpen(true);
  };
  const handleDialogClose = () => {
    setSubmitDialogOpen(false);
  };

  const handleCpoSubmissionHelpDialogOpen = () => {
    setCpoSubmissionHelpDialogOpen(true);
  };
  const handleCpoSubmissionHelpDialogClose = () => {
    setCpoSubmissionHelpDialogOpen(false);
  };

  const { data: blockData } = useQuery<GetBlockWithMarkersQueryResult>(
    GET_BLOCK_WITH_MARKERS_BY_ID,
    { variables: { id: blockId } },
  );

  const loadUserProfile = useCallback(async () => {
    try {
      const response = await auth?.loadUserProfile();
      setUserProfile(response);
    } catch (error) {
      auth?.logout();
    }
  }, [auth]);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  const handleSubmitPopUpClose = (event?: SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSubmitPopUpOpen(false);
  };

  const handleSubmitResponsePopUpClose = () => {
    setSubmitResponsePopUpOpen(false);
  };

  async function handleMonthSelected(dateRangeData: DatesSetArg): Promise<any> {
    if (blockData) {
      setFilteredMonthCpoDataBool(false);

      const newMonthNumber = dateRangeData.start.getMonth() + 1;
      const newYearNumber = dateRangeData.start.getFullYear();

      if (
        newMonthNumber === dateSelection.month &&
        newYearNumber === dateSelection.year
      ) {
        setRefreshTrigger((prev) => prev + 1);
      }

      const weekNumber1: number = getISOWeek(dateRangeData.start);
      console.log(
        `Date: ${format(
          dateRangeData.start,
          "yyyy-MM-dd",
        )}, ISO Week: ${weekNumber1}`,
      );

      setDateSelection({
        month: newMonthNumber,
        year: newYearNumber,
      });
      if (newYearNumber !== dateSelection.year) {
      setYearDateSelection({
        year: newYearNumber,
      });
      }

    } else {
      console.error("no BlockData available");
    }
  }

  useEffect(() => {
    async function fetchMonthData() {
      const { month, year } = dateSelection;
      if (blockData && month !== null && year !== null) {
        const blockQueryText = blockData?.block.name
          .replace(" ", "_")
          .toLowerCase();

        const url =
          process.env.REACT_APP_CPO_API_URL +
          // `http://localhost:8000`+
          `/api/files?month=${month}&year=${year}&block=${blockQueryText}`;
        // `/api/files?month=${month}&year=${year}&block=${blockData.block.name}`;

        try {
          const token = await auth?.getToken();
          const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                    }
                  });
          if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`);
            return;
          }
          const responseJson = await response.json();
          const jsonData = await JSON.parse(responseJson);
          const datesArray = jsonData.data.dates;

          console.log(datesArray);

          if (datesArray && datesArray.length > 0) {
            const processedDates = datesArray.map((dateObject) => {
              const assets = dateObject.assets || [];

              const uniqueAssetsMap = new Map();
              const uniqueAssetsArray = assets.reduce((acc, asset) => {
                const assetName = asset.assetName;

                if (!uniqueAssetsMap.has(assetName)) {
                  acc.push(asset);
                  uniqueAssetsMap.set(assetName, true);
                }
                return acc;
              }, []);

              return {
                ...dateObject,
                assets: uniqueAssetsArray,
              };
            });

            const uniqueData = {
              ...jsonData.data,
              dates: processedDates,
            };

            setMonthCpoData(uniqueData);
          } else {
            setMonthCpoData(jsonData.data);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          setFetchMonthDataError(true);
        }
      }
    }
    fetchMonthData();
    // eslint-disable-next-line
  }, [dateSelection, blockData, refreshTrigger]);

  useEffect(() => {
    async function fetchYearFiles() {
      const { year } = yearDateSelection;
      if (blockData && year !== null) {
        const blockQueryText = blockData?.block.name
          .replace(" ", "_")
          .toLowerCase();

        const url =
          process.env.REACT_APP_CPO_API_URL +
          // `http://localhost:8000`+
          `/api/specialFiles?block=${blockQueryText}&year=${year}`;
        // `/api/files?month=${month}&year=${year}&block=${blockData.block.name}`;

        try {
          const token = await auth?.getToken();
          const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    },
                  });
          if (!response.ok) {
            console.error(`HTTP error! status: ${response.status}`);
            return;
          }
          const responseJson = await response.json();
          // const jsonData = await JSON.parse(responseJson);

          console.log(responseJson.files);
          // console.log(jsonData);

          const mappedFiles = responseJson.files.map((file) => ({
            fileID: file.file_id,
            fileName: file.fileName || file.filename,
            documentType: file.document_type,
barrelLossCheck: file.barrelLossCheck || 0,
  barrelLossCheckTwoSigFig: file.barrelLossCheckTwoSigFig || "0.0",
  isQueued: file.isQueued || false,
  queueStatus: file.queueStatus || "None",
            blobContainerPath: file.blobContainerPath || file.blob_container_path,
          }));

          console.log(responseJson);
          console.log(mappedFiles);

          setYearCpoFileNames({ ...responseJson, files: mappedFiles });

          console.log(yearCpoFileNames);

        } catch (error) {
          console.error("Error fetching data:", error);
          setFetchMonthDataError(true);
        }
      }
    }
    fetchYearFiles();
    // eslint-disable-next-line
  }, [yearDateSelection, blockData, refreshTrigger]);

  useEffect(() => {
    if (monthCpoData && dateRangeSelected) {
      const processedFilesFromDB: CPOFileCalendarEntry[] = [];
      const failedCheckDatesFromDB: CPOFileCalendarEntry[] = [];
      const filesToFetch: string[] = [];
      const uniqueWeekNumbers = new Set<number>();

      const currentViewDate = dateRangeSelected.view.currentStart;

      const referenceDate =
        monthCpoData?.dates?.length > 0
          ? new Date(monthCpoData.dates[0].date)
          : currentViewDate;

      const currentBlock = monthCpoData?.blockName;

      const expectedReportsList =
        currentBlock && CPOConstants.EXPECTED_REPORTS_BY_BLOCK[currentBlock]
          ? CPOConstants.EXPECTED_REPORTS_BY_BLOCK[currentBlock]
          : CPOConstants.DEFAULT_EXPECTED_REPORTS;

      const dataByDate: Record<string, (typeof monthCpoData.dates)[0]> = {};
      if (monthCpoData?.dates) {
        monthCpoData.dates.forEach((dateObj) => {
          dataByDate[dateObj.date] = dateObj;
        });
      }

      const monthStart = startOfMonth(referenceDate);
      const monthEnd = endOfMonth(referenceDate);
      const allDaysInMonth = eachDayOfInterval({
        start: monthStart,
        end: monthEnd,
      });

      let tempTotalExpectedFiles = 0;
      let tempTotalReceivedFiles = 0;
      const tempAssetStats: Record<
        string,
        { expected: number; received: number }
      > = {};

      // Initialize the asset stats dictionary so it knows about all expected assets
      expectedReportsList.forEach((report) => {
        report.expectedAssets.forEach((asset) => {
          if (!tempAssetStats[asset]) {
            tempAssetStats[asset] = { expected: 0, received: 0 };
          }
        });
      });

      allDaysInMonth.forEach((day) => {
        const dayString = format(day, "yyyy-MM-dd");
        const todayData = dataByDate[dayString];

        tempTotalExpectedFiles += expectedReportsList.length;
        expectedReportsList.forEach((report) => {
          report.expectedAssets.forEach((asset) => {
            tempAssetStats[asset].expected += 1;
          });
        });

        if (todayData) {
          const dateFromString = new Date(todayData.date);
          const weekNumber = getISOWeek(dateFromString);
          todayData.weekNumber = weekNumber;
          uniqueWeekNumbers.add(weekNumber);

          todayData.unique_files.forEach((uniqueFile) => {
            todayData.files.forEach((file) => {
              if (file.fileName === uniqueFile.fileName) {
                uniqueFile.barrelLossCheck = file.barrelLossCheck;
              }
              if (
                file.fileName &&
                azureUploadRecordMap[file.fileName] === undefined &&
                !filesToFetch.includes(file.fileName)
              ) {
                filesToFetch.push(file.fileName);
              }
            });

          });

          const actualAssetNames = new Set(
            todayData.assets.map((a) => a.assetName),
          );

          expectedReportsList.forEach((report) => {
            let isReportMissing = false;
            const reportNameLower = report.reportDisplayName.toLowerCase();

            let associatedFileNames: string[] = [];

            if (reportNameLower.includes("daily ops")) {
              const hasDailyOpsFile = todayData.assets.some(
                (a) => a.dailyOpsFile && a.dailyOpsFile.trim() !== "",
              );
              isReportMissing = !hasDailyOpsFile;

              associatedFileNames = todayData.assets
                .map((a) => a.dailyOpsFile)
                .filter(Boolean);
            } else if (
              reportNameLower.includes("production daily") ||
              reportNameLower.includes("prod daily")
            ) {
              const hasProdDailyFile = todayData.assets.some(
                (a) => a.prodDailyFile && a.prodDailyFile.trim() !== "",
              );
              isReportMissing = !hasProdDailyFile;

              associatedFileNames = todayData.assets
                .map((a) => a.prodDailyFile)
                .filter(Boolean);
            } else {
              isReportMissing = report.expectedAssets.some(
                (expectedAsset) => !actualAssetNames.has(expectedAsset),
              );
              todayData.assets.forEach((a) => {
                if (report.expectedAssets.includes(a.assetName)) {
                  if (a.prodDailyFile && a.prodDailyFile.trim() !== "") {
                    associatedFileNames.push(a.prodDailyFile);
                  }
                  if (a.dailyOpsFile && a.dailyOpsFile.trim() !== "") {
                    associatedFileNames.push(a.dailyOpsFile);
                  }
                }
              });
            }

            const queuedFile = todayData.unique_files.find((file) => {
              if (!file.isQueued && file.queueStatus.toLowerCase() !== "failed") {
                return false;
              }

              const actualFileName = file.fileName;

              if (!actualFileName) {
  return false;
}

              const fileNameLower = actualFileName.toLowerCase();

              if (associatedFileNames.length > 0) {
                return associatedFileNames.includes(actualFileName);
              }

              if (
                reportNameLower.includes("prod daily") ||
                reportNameLower.includes("production daily")
              ) {
                return (
                  fileNameLower.includes("summary report") ||
                  fileNameLower.includes("production")
                );
              }
              if (reportNameLower.includes("daily ops")) {
                return (
                  fileNameLower.includes("daily ops") ||
                  fileNameLower.includes("ops")
                );
              }
              if (reportNameLower.includes("saxi")) {
  const normalisedFileName = fileNameLower.replace(/\s+/g, "");
  const normalisedReportName = reportNameLower.replace(/\s+/g, "");
  return normalisedFileName.includes(normalisedReportName);
}

              return fileNameLower.includes(reportNameLower);
            });

            console.log(queuedFile);

            if (queuedFile) {
              // Report is queued, display the queue status
              const isFailed =
                queuedFile.queueStatus.toLowerCase() === "failed";
              processedFilesFromDB.push({
                title: `${report.reportDisplayName} - ${queuedFile.queueStatus}`,
                start: dayString,
                display: `auto`,
                color: isFailed ? "#e74c3c" : "#f39c12",
              });
            } else if (!isReportMissing) {
              tempTotalReceivedFiles += 1; // Increment overall file stats

              let isLowConfidence = false;

              todayData.assets.forEach((asset) => {
                const hasValidProdFile = asset.prodDailyFile && asset.prodDailyFile.trim() !== "";
                const hasValidOpsFile = asset.dailyOpsFile && asset.dailyOpsFile.trim() !== "";

                const isAssetInThisReport =
                  (hasValidProdFile && associatedFileNames.includes(asset.prodDailyFile)) ||
                  (hasValidOpsFile && associatedFileNames.includes(asset.dailyOpsFile));

                const confidenceLevelNum = Number(asset.confidenceLevel);

                if (isAssetInThisReport && !isNaN(confidenceLevelNum) && confidenceLevelNum < 0.7) {
                  isLowConfidence = true;
                }
              });

              console.log(report.reportDisplayName + isLowConfidence);

              processedFilesFromDB.push({
                title: report.reportDisplayName,
                start: dayString,
                display: `auto`,
                color: isLowConfidence ? "#63ceff" : "auto",
              });
            } else {
              processedFilesFromDB.push({
                title: `${report.reportDisplayName}` + t("missing"),
                start: dayString,
                display: `auto`,
                color: "#8b8b8b",
              });
            }

            report.expectedAssets.forEach((expectedAsset) => {
              if (actualAssetNames.has(expectedAsset)) {
                tempAssetStats[expectedAsset].received += 1;
              }
            });
          });

          // todayData.assets.forEach((asset) => {
          //   const barrelLossNumber = Number(asset.barrelLoss);
          //   asset.barrelLossTwoSigFig = barrelLossNumber.toFixed(2);
          //   if (asset.confidenceLevel !== null && asset.confidenceLevel < 0.7) {
          //     failedCheckDatesFromDB.push({
          //       title: "",
          //       start: dayString,
          //       display: `background`,
          //       color: Colors.CPO_SUBMISSION_DATE_CHECKS_BAD,
          //     });
          //   } else if (isNaN(asset.confidenceLevel)) {
          //     failedCheckDatesFromDB.push({
          //       title: "",
          //       start: dayString,
          //       display: `background`,
          //       color: Colors.CPO_SUBMISSION_DATE_CHECKS_NaN,
          //     });
          //   }
          // });
        } else {
          // Render exactly one grey pill for each expected report configured in the block
          expectedReportsList.forEach((report) => {
            processedFilesFromDB.push({
              title: `${report.reportDisplayName}` + t("missing"),
              start: dayString,
              display: `auto`,
              color: "#8b8b8b",
            });
          });
        }
      });

      monthCpoData.uniqueWeekNumbers = Array.from(uniqueWeekNumbers);

      setProcessedFilesForCalendar(processedFilesFromDB);
      setFailedCheckDatesForCalendar(failedCheckDatesFromDB);

      setMonthStats({
        totalExpectedFiles: tempTotalExpectedFiles,
        totalReceivedFiles: tempTotalReceivedFiles,
        assetStats: tempAssetStats,
      });

      // Fire the single batch request if we have files to check
      if (filesToFetch.length > 0) {
        fetchAzureUploadRecordsBatch(filesToFetch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthCpoData, setProcessedFilesForCalendar, dateRangeSelected]);

  const [CPOFilesToBeProcessed, setCPOFilesToBeProcessed] = useState<
    CPOFileWithCheck[]
  >([]);

  async function fetchAlreadyProcessedCheck(
    updatedFilesNames: string[],
    updatedFiles: CPOFileWithCheck[],
  ): Promise<CPOFileWithCheck[]> {
    const updatedFilesWithCheck: CPOFileWithCheck[] = [];
    const checkUrl = process.env.REACT_APP_CPO_API_URL + `/api/check`;
    // const checkUrl =`http://localhost:8000/api/check`;
    try {
      const token = await auth?.getToken();
      const response = await fetch(checkUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedFilesNames),
      });

      console.log(response);

      const data: {
        filenames: string[];
        check: boolean[];
        secureFileUrl: string;
      } = await response.json();

      const url = data.secureFileUrl;

      const googleViewSecureUrl =
        "http://docs.google.com/gview?url=" + { url } + "&embedded=true";

      console.log(googleViewSecureUrl);

      updatedFiles.forEach((updatedFile) => {
        const index = data.filenames.indexOf(updatedFile.file.name);
        if (index !== -1) {
          updatedFilesWithCheck.push({
            file: updatedFile.file,
            alreadyProcessed: data.check[index],
            status: "checked",
            errorMessage: "",
          });
        }
      });
      return updatedFilesWithCheck;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  async function fetchAzureUploadRecordsBatch(
    fileNames: string[],
  ): Promise<void> {
    const fetchBatchUrl =
      process.env.REACT_APP_CPO_API_URL + `/api/fetchAzureUploadRecordsBatch`;
    // const fetchBatchUrl = `http://localhost:8000/api/fetchAzureUploadRecordsBatch`;

    try {
      const requestBody = {
        file_names: fileNames,
      };

      const token = await auth?.getToken();
      const response = await fetch(fetchBatchUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody),
      });

      console.log(
        "fetchAzureUploadRecordsBatch response status:",
        response.status,
      );

      // Handle HTTP errors
      if (!response.ok) {
        console.error(`Backend returned an error: ${response.status}`);
        setAzureUploadRecordMap((prevMap) => {
          const fallbackMap = { ...prevMap };
          fileNames.forEach((name) => (fallbackMap[name] = false));
          return fallbackMap;
        });
        return;
      }

      const data = await response.json();
      const batchResults = data.batch_results;

      console.log("Success: Batch results received.", batchResults);

      // Update state with the returned objects or false
      setAzureUploadRecordMap((prevMap) => {
        const updatedMap = { ...prevMap };

        fileNames.forEach((fileName) => {
          const record = batchResults[fileName];
          // If the backend returned a safe_record, save it. Otherwise, save false.
          updatedMap[fileName] =
            record !== null && record !== undefined ? record : false;
        });

        return updatedMap;
      });
    } catch (error) {
      console.error("Network / parsing error during batch fetch:", error);
      // Fallback on network failure
      setAzureUploadRecordMap((prevMap) => {
        const fallbackMap = { ...prevMap };
        fileNames.forEach((name) => (fallbackMap[name] = false));
        return fallbackMap;
      });
    }
  }

  const handleCpoFilesSelected = async (files: File[]) => {
    let updatedFiles: CPOFileWithCheck[] = [];
    setCPOFilesToBeProcessed((prevFiles) => {
      updatedFiles = prevFiles ? [...prevFiles] : [];
      files.forEach((file) => {
        const isDuplicate = updatedFiles.some(
          (existingFile) => existingFile.file.name === file.name,
        );
        const fileToPush: CPOFileWithCheck = {
          file,
          alreadyProcessed: false,
          status: "selected",
          errorMessage: "",
        };
        if (!isDuplicate) {
          updatedFiles.push(fileToPush);
        }
      });
      return updatedFiles;
    });

    const updatedFilesNames = updatedFiles.map(
      (updatedFile) => updatedFile.file.name,
    );

    const filesToShow = await fetchAlreadyProcessedCheck(
      updatedFilesNames,
      updatedFiles,
    );
    setCPOFilesToBeProcessed(filesToShow);
  };

  const handleRemoveCPOFilesToBeProcessed = (
    filesToRemove: CPOFileWithCheck | CPOFileWithCheck[],
  ) => {
    setCPOFilesToBeProcessed((prevFiles) => {
      if (!prevFiles) { return []; }
      let namesToRemove: string[] = [];
      if (Array.isArray(filesToRemove)) {
        namesToRemove = filesToRemove.map(
          (fileToRemove) => fileToRemove.file.name,
        );
      } else {
        namesToRemove = [filesToRemove.file.name];
      }
      return prevFiles.filter(
        (prevFile) => !namesToRemove.includes(prevFile.file.name),
      );
    });
  };

  const handleSubmitCPOFilesToBeProcessed = async (uploadOnly: boolean) => {
    const filesToSubmit = dataToBeProcessed;

    if (filesToSubmit === undefined) {
      console.log("filesToSubmit is undefined");
      return;
    }

    const startRaw = new Date();
    const startTime = startRaw.getTime();
    let uploadFinishedTime = 0;

    let filesArray: CPOFileWithCheck[] = [];

    try {
      setDisableSubmitDeleteButtons(true);
      setTableItemsSelectable(false);
      setSubmitPopUpOpen(true);
      handleDialogClose();
      setDropzoneDisabledBool(true);
      setUploadLoadingSpinner(true);

      setCPOFilesToBeProcessed((prev) =>
        prev.map((item) => ({ ...item, status: "processing" })),
      );

      if (Array.isArray(filesToSubmit)) {
        filesArray = filesToSubmit;
      } else {
        filesArray = [filesToSubmit];
      }

      const dataTransfer = new DataTransfer();

      if (uploadOnly === true) {
        filesArray.forEach((cpoFileWithCheck) => {
          if (cpoFileWithCheck.alreadyProcessed === true) {
            dataTransfer.items.add(cpoFileWithCheck.file);
          }
        });
      } else {
        filesArray.forEach((cpoFileWithCheck) => {
          dataTransfer.items.add(cpoFileWithCheck.file);
        });
      }

      const fileList: FileList = dataTransfer.files;

      let fileCount = 0;

      const formData = new FormData();
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList.item(i);
        if (
          file &&
          (file.type === "application/pdf" ||
            file.type === "application/vnd.ms-excel" ||
            file.type ===
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
        ) {
          formData.append("files", file);
          fileCount++;
        } else if (file) {
          alert(`File ${file.name} is not a valid file${file.type}`);
        }
      }

      console.log(formData);

      console.log(`Total PDF files added to FormData: ${fileCount}`);

      const blockQueryText = blockData?.block.name
        .replace(" ", "_")
        .toLowerCase();

      const cpoFileUploader =
        userProfile?.firstName && userProfile?.lastName
          ? `${userProfile.firstName} ${userProfile.lastName}`
          : "Unknown User";
      if (cpoFileUploader) {
        formData.append("cpoFileUploaderName", cpoFileUploader);
      }

      let uploadUrl = "";

      if (uploadOnly === true && auth?.hasRealmRole(Roles.administrator)) {
        uploadUrl =
          process.env.REACT_APP_CPO_API_URL +
          // `http://localhost:8000` +
          `/api/upload/filetoazureonly`;
        if (blockQueryText) {
          const uploadOnlyBlockQueryText = blockQueryText.replace("_", "-");
          formData.append("blockName", uploadOnlyBlockQueryText);
        }
      } else {
        uploadUrl =
          process.env.REACT_APP_CPO_API_URL +
          // `http://localhost:8000` +
          `/api/upload/block${blockQueryText}`;
      }

      formData.forEach((value, key) => {
        if (value instanceof File) {
          console.log(
            `${key}: File - Name: ${value.name}, Type: ${value.type}, Size: ${value.size} bytes`,
          );
        } else {
          console.log(`Key: ${key} | Type: Text | Value: ${value}`);
        }
      });

      console.log(formData);
      const token = await auth?.getToken();
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          'Authorization': `Bearer ${token}`
        },
        timeout: 270000,
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 1;
          const current = progressEvent.loaded;
          const percentage = Math.floor((current * 100) / total);

          if (progressEvent.progress === 1) {
            uploadFinishedTime = Date.now();
          }

          setUploadProgress(percentage);
        },
      });

      const endRaw = new Date();
      const endTime = endRaw.getTime();

      const uploadDuration = Math.round(
        (uploadFinishedTime - startTime) / 1000,
      );
      const processingDuration = Math.round(
        (endTime - uploadFinishedTime) / 1000,
      );

      setUploadSummary({
        totalFiles: filesArray.length,
        uploadTime: uploadDuration > 0 ? uploadDuration : 0,
        processingTime: processingDuration,
        totalTime: Math.round((endTime - startTime) / 1000),
        startDateTime: startRaw.toLocaleTimeString(),
        endDateTime: endRaw.toLocaleTimeString(),
      });

      const submitResponse = response.data;
      console.log("Server Response:", submitResponse);

      if (
        submitResponse &&
        submitResponse.files &&
        submitResponse.files.length > 0
      ) {
        const results: CPOFileJustProcessed[] = submitResponse.files.map(
          (res: any) => {
            const originalFile = filesArray.find(
              (f) => f.file.name.toLowerCase() === res.filename.toLowerCase(),
            );

            return {
              ...res,
              fileSize: originalFile ? originalFile.file.size : 0,
            };
          },
        );
        setJustProcessedFiles(results);

        const namesToRemove = results
          .filter((res) => res.status === "success")
          .map((res) => res.filename);

        console.log("Names to remove:", namesToRemove);

        console.log("Local names:", namesToRemove);
        console.log(
          "State names:",
          CPOFilesToBeProcessed.map((f) => f.file.name),
        );

        setCPOFilesToBeProcessed((prevFiles) =>
          prevFiles.filter((file) => !namesToRemove.includes(file.file.name)),
        );

        setCPOFilesToBeProcessed((prevFiles) =>
          prevFiles.map((file) => {
            const errorDetail = submitResponse.files.find(
              (res: any) =>
                res.fileName === file.file.name && res.status === "error",
            );
            return errorDetail
              ? {
                  ...file,
                  status: "error",
                  errorMessage: errorDetail.message || errorDetail.error,
                }
              : file;
          }),
        );
      }
      setSubmitResponsePopUpOpen(true);
    } catch (error) {
      const err = error as any;
      console.error("Upload error caught:", error);

      // This checks if the error is a timeout (from Axios or Network Throttling)
      if (
        axios.isCancel(error) ||
        err.code === "ECONNABORTED" ||
        err.message.includes("timeout")
      ) {
        const timeoutMockData: CPOFileJustProcessed[] = filesArray.map((f) => ({
          filename: f.file.name,
          fileSize: f.file.size,
          status: "warning",
          ai_status: "queued",
          upload_status: "skipped",
          message:
            "The server is taking a long time to respond. Your files might still be processing in the background. Please check back in a few minutes.",
          error: "Request Timeout",
          checks: [],
          block_check: "failed",
        }));

        setJustProcessedFiles(timeoutMockData);

        const endRaw = new Date();
        const endTime = endRaw.getTime();
        setUploadSummary((prev) => ({
          ...prev,
          totalFiles: filesArray.length,
          uploadTime:
            uploadFinishedTime > 0
              ? Math.round((uploadFinishedTime - startTime) / 1000)
              : 0,
          processingTime:
            uploadFinishedTime > 0
              ? Math.round((endTime - uploadFinishedTime) / 1000)
              : 0,
          startDateTime: startRaw.toLocaleTimeString(),
          endDateTime: endRaw.toLocaleTimeString(),
          totalTime: Math.round((endRaw.getTime() - startTime) / 1000),
        }));

        setCPOFilesToBeProcessed((prev) =>
          prev.map((file) => {
            // If the file was in the middle of being processed, mark it as warning
            if (file.status === "processing") {
              return {
                ...file,
                status: "warning",
                errorMessage:
                  "Server connection timed out. Status is uncertain.",
              };
            }
            return file;
          }),
        );

        setSubmitResponsePopUpOpen(true);
      } else {
        // Handle standard 400/500 errors
        setCPOFilesToBeProcessed((prev) =>
          prev.map((file) => ({ ...file, status: "error" })),
        );
      }
    } finally {
      setDisableSubmitDeleteButtons(false);
      setDropzoneDisabledBool(false);
      setTableItemsSelectable(true);
      setUploadLoadingSpinner(false);
      setUploadProgress(0);
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;

    // Start timer ONLY when upload is done but we are still waiting for the server
    if (uploadLoadingSpinner && uploadProgress === 100) {
      interval = setInterval(() => {
        setProcessingSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      setProcessingSeconds(0);
    }

    return () => clearInterval(interval);
  }, [uploadLoadingSpinner, uploadProgress]);

  const handleFilterCpoMonthData = (dateRangeSelected) => {
    if (monthCpoData) {
      const startRange = new Date(dateRangeSelected.startStr);
      startRange.setHours(0, 0, 0, 0);

      const adjustedEndRange = new Date(dateRangeSelected.endStr);
      adjustedEndRange.setDate(adjustedEndRange.getDate() - 1);
      adjustedEndRange.setHours(23, 59, 59, 999);

      const filteredDatesArray = monthCpoData.dates.filter((dateItem) => {
        if (!dateItem || !dateItem.date) {
          return false;
        }

        const currentDate = new Date(dateItem.date);
        currentDate.setHours(0, 0, 0, 0);
        return currentDate >= startRange && currentDate <= adjustedEndRange;
      });
      const newFilteredCpoData: CPOMonthData = {
        ...monthCpoData,
        dates: filteredDatesArray,
      };
      console.log("newFilteredCpoData");
      console.log(newFilteredCpoData);

      setFilteredMonthCpoData(newFilteredCpoData);
      setFilteredMonthCpoDataBool(true);
    }
  };

  const handleDeferralDateClick = (originalRowData: CPOMonthDataDate) => {
    window.scrollTo(0, 0);

    const serializableRowData = {
      date: originalRowData.date,
    };

    const cpoFileSubmissionRowData = {
      rowData: serializableRowData,
    };

    if (Array.isArray(originalRowData)) {
      history.push({
        pathname: `/block/${blockId}/${Routes.CPO_DEFERRAL_BASEPATH}`,
        state: cpoFileSubmissionRowData,
      });
    } else {
      history.push({
        pathname: `/block/${blockId}/${Routes.CPO_DEFERRAL_BASEPATH}`,
        state: cpoFileSubmissionRowData,
      });
    }
  };

  const handleSummaryReportDownloadClick = () => {
    downloadProcessingReport({
      uploadSummary,
      justProcessedFiles,
      blockName: blockData?.block.name || "Unknown Block",
      userName: userProfile?.firstName + " " + userProfile?.lastName,
    });
  };

  const fetchFileUrl = useCallback(
    async (
      file: CPOMonthDataFile,
      blockData: GetBlockWithMarkersQueryResult,
    ) => {
      const url = process.env.REACT_APP_CPO_API_URL + `/api/getFileUrl`;
      // const url =`http://localhost:8000/api/getFileUrl`;

      let blockNameForFileUrl = blockData.block.name;
      if (blockData.block.name === "2_05") {
        blockNameForFileUrl = "2-05";
      } else if (blockData.block.name === "FS_FST") {
        blockNameForFileUrl = "fs-fst";
      } else if (blockData.block.name === "3_05") {
        blockNameForFileUrl = "3-05";
      } else if (blockData.block.name === "17_06") {
        blockNameForFileUrl = "17-06";
      } else if (blockData.block.name === "15_06") {
        blockNameForFileUrl = "15-06";
      }

      let fileTypeAndOrMonth;

      if (file.blobContainerPath && (file.blobContainerPath.includes("monthly_production") ||
      (file.blobContainerPath.includes("vulnerabilities")) ||
      (file.blobContainerPath.includes("contracts")))) {
        fileTypeAndOrMonth = file.blobContainerPath.split("/").slice(1, 3).join("/");
      } else  {
        fileTypeAndOrMonth = dateSelection.month;
      }

      let requestBody;
      const fileName = file.fileName;

      if (file.documentType === "monthly_production"
        || file.documentType === "vulnerabilities"
        || file.documentType === "contracts") {
          requestBody = {
            file_id: file.fileID,
          };
        } else {
      requestBody = {
        block_name: blockNameForFileUrl,
        file_name: fileName,
        month_number: fileTypeAndOrMonth,
        year_number: dateSelection.year,
      };
        }

      console.log(requestBody);

      try {
        const token = await auth?.getToken();
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          return;
        }
        const responseJson = await response.json();

        if (responseJson && responseJson.secureFileUrl) {

          window.open(responseJson.secureFileUrl, "_blank");
        } else {
          console.error("File URL not found in API response.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    // eslint-disable-next-line
    [dateSelection],
  );

  const handleGetFileUrlClick = (file: CPOMonthDataFile) => {
    if (blockData) {
      fetchFileUrl(file, blockData);
    }
  };

  // Helper function to generate the Tooltip content for any given date
  const renderTooltipContent = (dateObj: Date) => {
    const dayString = format(dateObj, "yyyy-MM-dd");

    const dayEvents = processedFilesForCalendar.filter(
      (e) => e.start === dayString,
    );
    const failedChecks = failedCheckDatesForCalendar.filter(
      (e) => e.start === dayString,
    );

    // If there is nothing to show, return null
    if (dayEvents.length === 0 && failedChecks.length === 0) { return null; }

    const hasMissingFiles = dayEvents.some((e) => e.title.includes(t("missing")));

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          padding: "4px",
        }}
      >
        <Typography
          variant="subtitle2"
          style={{ textDecoration: "underline", marginBottom: "4px" }}
        >
          {format(dateObj, "MMMM do, yyyy")} Summary
        </Typography>

        {!hasMissingFiles && dayEvents.length > 0 && (
          <Typography
            variant="caption"
            style={{
              color: "#04ad6f",
              fontWeight: "bold",
              fontStyle: "italic",
              marginBottom: "4px",
            }}
          >
            {/* ✓ All expected files accounted for */}
          </Typography>
        )}

        {dayEvents.map((event, index) => {
          const isMissing = event.title.includes(t("missing"));
          const isLowConfidence = event.color === "#63ceff" && !isMissing;

          let textColor = "#333333";
          let displayText = event.title;

          if (isMissing) {
            textColor = "#e44848";
            displayText = event.title;
          } else if (isLowConfidence) {
            textColor = "#63ceff";
            displayText = event.title + " - Low or no confidence level";
          }

          return (
            <Typography
              key={`event-${index}`}
              variant="caption"
              style={{
                color: textColor,
                fontWeight: "bold",
              }}
            >
              • {displayText}
            </Typography>
          );
        })}

        {failedChecks.length > 0 && (
          <>
            <hr
              style={{ width: "100%", borderColor: "#555", margin: "4px 0" }}
            />
            <Typography variant="caption" style={{ color: "#fabf41" }}>
              Check(s) Failed
            </Typography>
          </>
        )}
      </div>
    );
  };

  const allCalendarEvents = [
    ...(processedFilesForCalendar || []),
    ...(selectedDateForCalendar || []),
    ...(failedCheckDatesForCalendar || []),
  ];

  const initialCalendarDate =
    dateSelection.year !== null && dateSelection.month !== null
      ? `${dateSelection.year}-${String(dateSelection.month).padStart(2, "0")}-01`
      : undefined;

  if (
    !auth?.hasRealmRole(Roles.auditor) &&
    !auth?.hasRealmRole(Roles.administrator) &&
    !auth?.hasRealmRole(Roles.cpoFileSubmissionViewer) &&
    !auth?.hasRealmRole(Roles.cpoFileSubmitter)
  ) {
    return null;
  }

  return (
    <div className={classes.root}>
        {blockData?.block.name &&
        enabledCpoSubmissionBlocks &&
        enabledCpoSubmissionBlocks.includes(blockData?.block.name) &&
        fetchMonthDataError === false &&
        (auth?.hasRealmRole(Roles.cpoFileSubmitter) ||
          auth?.hasRealmRole(Roles.administrator) ||
          auth?.hasRealmRole(Roles.cpoFileSubmissionViewer)) ? (
          <div>
            <Grid container>
              <Grid item xs={12} className={classes.infoDisplay}>
                <Paper>
                  {/* <Grid item xs={12} className={classes.infoDisplay}> */}
                  <Grid container className={classes.monthTitle}>
                    <Typography variant="h6">
                      {t("block") +
                        blockData?.block.name +
                        t("cpoProcessingTitle") +
                        dateRangeSelected?.start.toLocaleString("default", {
                          month: "long",
                        }) +
                        " " +
                        dateRangeSelected?.start.toLocaleString("default", {
                          year: "numeric",
                        })}
                    </Typography>

                    {monthStats && (
                      <Grid item>
                        <Chip
                          label={t("totalFilesForMonth") + `${monthStats.totalReceivedFiles} / ${monthStats.totalExpectedFiles}`}
                          color={
                            monthStats.totalReceivedFiles ===
                            monthStats.totalExpectedFiles
                              ? "primary"
                              : "default"
                          }
                          style={{ fontWeight: "bold" }}
                        />
                      </Grid>
                    )}

                    {monthStats &&
                      Object.entries(monthStats.assetStats).map(
                        ([assetName, stats]) => {
                          const isComplete = stats.received === stats.expected;
                          return (
                            <Grid item key={assetName}>
                              <Chip
                                // size="small"
                                variant={isComplete ? "default" : "default"}
                                label={`${assetName}: ${stats.received}/${stats.expected}`}
                                style={{
                                  borderColor: isComplete ? "green" : "gray",
                                  color: isComplete ? "green" : "inherit",
                                }}
                              />
                            </Grid>
                          );
                        },
                      )}

                    <Tooltip title={"CPO File Submission Help"} arrow={true}>
                      <Button
                        onClick={() => handleCpoSubmissionHelpDialogOpen()}
                      >
                        <QuestionMarkIcon />
                      </Button>
                    </Tooltip>
                  </Grid>

                  <Dialog
                    open={cpoSubmissionHelpDialogOpen}
                    onClose={handleCpoSubmissionHelpDialogClose}
                    aria-labelledby="dialog-title"
                    aria-describedby="dialog-description"
                    fullWidth={true}
                    maxWidth={"xl"}
                  >
                    <DialogTitle id="help-dialog-title">
                      <h2>{"CPO File Submission Help"}</h2>
                    </DialogTitle>

                    <DialogContent>
                      <DialogContentText id="dialog-description">
                        <Grid container>
                          <Grid item xs={12} className={classes.helpDialog}>
                            <h2>
                              <strong>
                                {"Block "}
                                {blockData.block.name}
                                {" Specific Help"}
                              </strong>
                            </h2>
                            {blockData.block.name === "15" && (
                              <Typography
                                variant={"h4"}
                                style={{
                                  backgroundColor: "blue",
                                  color: "white",
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                  display: "inline-block",
                                }}
                              >
                                <i>
                                  {"For Block "}
                                  {blockData.block.name}
                                  {": "}
                                  <u>
                                    <b>{"4 Daily Reports per Asset"}</b>
                                  </u>
                                  {" and "}
                                  <u>
                                    <b>{"1 Summary Report"}</b>
                                  </u>
                                  {
                                    " is needed for full processing of data for "
                                  }
                                  <u>
                                    <b>{"each day."}</b>
                                  </u>
                                </i>
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid item xs={4} className={classes.helpDialog}>
                            <h2>
                              <strong>
                                {"Left Hand Side Table and File Drop Zone"}
                              </strong>
                            </h2>
                            <li>
                              {
                                "Multiple files can be added to the upload files table using the file drop zone at the bottom left of the page."
                              }
                            </li>
                            <li>
                              {
                                "These files can the be selected and either removed from the list or submitted for processing."
                              }
                            </li>
                            <li>
                              {
                                "Once processing has begun, this table and drop zone is disabled until a success or error response has been received."
                              }
                            </li>
                            <li>
                              <strong>
                                {
                                  "Please Note: The more files submitted at one time, the longer the processing will take."
                                }
                              </strong>
                            </li>
                          </Grid>
                          <Grid item xs={4} className={classes.helpDialog}>
                            <h2>
                              <strong>{"Calendar"}</strong>
                            </h2>
                            <li>
                              {
                                "The calendar can be scrolled through to quickly see the files processed on a day within a selected month at a glance."
                              }
                            </li>
                            <li>
                              {
                                "A RED higlighted day indicates that the confidence level for at least one of the files processed that day is less than a set minimum."
                              }
                            </li>
                            <li>
                              {
                                "A YELLOW/ORANGE highlighted day indicates that the confidence level for at least one of the files processed that day is unknown / undetermined."
                              }
                            </li>
                            <li>
                              {
                                "One or multiple days can be selected from the calendar by clicking on a day and dragging across other days in the calendar. This filters the right hand side table to only show the selected days."
                              }
                            </li>
                          </Grid>

                          <Grid item xs={4} className={classes.helpDialog}>
                            <h2>
                              <strong>{"Right Hand Side Table"}</strong>
                            </h2>
                            <li>
                              {
                                "At a top level view, the processed dates table shows the date, total barrel loss for that date, and the minimum confidence level out of all files processed for that day."
                              }
                            </li>
                            <li>
                              {
                                "A RED higlighted day indicates that the confidence level for at least one of the files processed that day is less than a set minimum."
                              }
                            </li>
                            <li>
                              {
                                "A YELLOW/ORANGE highlighted day indicates that the confidence level for at least one of the files processed that day is unknown / undetermined."
                              }
                            </li>
                            <li>
                              {
                                "A GREEN highlighted day indicates that the confidence level for all of the files processed that day is greater than a set minimum for each file."
                              }
                            </li>
                            <li>
                              {
                                "In the detailed view for a day, values for individual assets are shown as well as the list of file names processed for that day."
                              }
                            </li>
                          </Grid>
                        </Grid>
                      </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                      <Button
                        onClick={() => handleCpoSubmissionHelpDialogClose()}
                        color="primary"
                      >
                        {"Close"}
                      </Button>
                    </DialogActions>
                  </Dialog>

                  {/* <Typography variant="h4">
                      {"Summary of Month Data"}
                    </Typography>
                    <Typography variant="body1">
                      {
                        "Data from summary of processed files, and data from month's individual report"
                      }
                    </Typography> */}
                  {/* </Grid> */}
                </Paper>
              </Grid>
              {uploadLoadingSpinner && (
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  my={2}
                  width="100%"
                >
                  <Box width="100%" mb={1}>
                    <LinearProgress
                      variant={
                        uploadProgress < 100 ? "determinate" : "indeterminate"
                      }
                      value={uploadProgress}
                    />
                  </Box>
                  <Paper>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      align="center"
                    >
                      {uploadProgress < 100
                        ? `Uploading: ${uploadProgress}%`
                        : `Processing & Extracting Data... (${processingSeconds}s elapsed)`}
                    </Typography>
                    {uploadProgress === 100 && (
                      <Typography variant="caption" color="textSecondary">
                        Larger files may take a few minutes to process. Please
                        don't refresh.
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )}
              {(auth?.hasRealmRole(Roles.cpoFileSubmitter) ||
                auth?.hasRealmRole(Roles.administrator)) && (
                <Grid item xs={4} className={classes.left}>
                  <div className={classes.table}>
                    <FileDropZone
                      handleFilesSelected={handleCpoFilesSelected}
                      allowMultipleFiles={true}
                      cpoFileDropZone={true}
                      acceptedFileTypes={
                        ValidationConstants.ACCEPTED_CPO_FILE_TYPES_BLOCKS_WITH_EXCEL_TYPES
                      }
                      dropzoneDisabledBool={dropzoneDisabledBool}
                    >
                      {(openFileBrowser) => (
                        <MaterialTable
                          style={{ height: "100%" }}
                          options={{
                            draggable: false,
                            selection: tableItemsSelectable,
                            selectionProps: {
                              disabled: disableSubmitDeleteButtons,
                            },
                            padding: "default",
                            pageSizeOptions: [],
                            showFirstLastPageButtons: false,
                            pageSize: 10,
                            tableLayout: "auto",
                            search: false,
                            rowStyle: (rowData) => ({
                              backgroundColor:
                                rowData.alreadyProcessed === true
                                  ? "rgba(255, 115, 0, 0.77)"
                                  : "rgba(0, 100, 250, 0.6)",
                            }),
                          }}
                          title={
                            t("uploadFilesForProcessing") +
                            blockData?.block.name
                          }
                          data={CPOFilesToBeProcessed}
                          columns={[
                            {
                              title: t("statusUploadTable"),
                              render: (rowData) => (
                                <>
                                  {uploadLoadingSpinner === true &&
                                    rowData.status !== "error" && (
                                      <CircularProgress />
                                    )}
                                  {rowData.status === "error" && (
                                    <Tooltip
                                      title={
                                        rowData.errorMessage || "Upload failed"
                                      }
                                    >
                                      <ErrorIcon style={{ color: "red" }} />
                                    </Tooltip>
                                  )}
                                  {rowData.status === "warning" && (
                                    <Tooltip
                                      title={
                                        rowData.errorMessage || "Timeout error"
                                      }
                                    >
                                      <WarningIcon style={{ color: "black" }} />
                                    </Tooltip>
                                  )}
                                </>
                              ),
                              cellStyle: {
                                width: "5%",
                                minWidth: "30px",
                                maxWidth: "80px",
                              },
                              headerStyle: {
                                width: "5%",
                                minWidth: "30px",
                                maxWidth: "80px",
                              },
                            },
                            {
                              title: t("fileNameUploadTable"),
                              field: "file.name",
                              cellStyle: {
                                width: "50%",
                                minWidth: "200px",
                                maxWidth: "800px",
                              },
                              headerStyle: {
                                width: "50%",
                                minWidth: "200px",
                                maxWidth: "800px",
                              },
                            },
                            {
                              title: t("alreadyProcessedUploadTable"),
                              field: "alreadyProcessed",

                              cellStyle: {
                                width: "5%",
                                minWidth: "80px",
                                maxWidth: "300px",
                              },
                              headerStyle: {
                                width: "5%",
                                minWidth: "80px",
                                maxWidth: "300px",
                              },
                              render: (rowData) => (
                                <>
                                  {rowData.alreadyProcessed === true ? (
                                    <Tooltip
                                      title={t<string>(
                                        t("alreadyProcessedTooltip"),
                                      )}
                                      arrow={true}
                                    >
                                      <div>?</div>
                                    </Tooltip>
                                  ) : (
                                    <>No</>
                                  )}
                                </>
                              ),
                            },
                          ]}
                          icons={TableIcons}
                          actions={[
                            {
                              icon: () => <AddIcon />,
                              tooltip: t("browseForFilesUploadTable"),
                              isFreeAction: true,
                              onClick: () => openFileBrowser(),
                            },
                            {
                              icon: () => <DeleteIcon />,
                              disabled: disableSubmitDeleteButtons,
                              tooltip:
                                "Remove Selected Documents from List to be Processed",
                              onClick: (evt, data) => {
                                handleRemoveCPOFilesToBeProcessed(data);
                              },
                            },
                            {
                              icon: () => <PublishIcon />,
                              disabled: disableSubmitDeleteButtons,
                              tooltip:
                                "Submit Selected Documents from List to be Processed",
                              onClick: (evt, data) => {
                                setDataToBeProcessed(data);
                                if (data) {
                                  handleDialogOpen();
                                }
                              },
                            },
                          ]}
                          localization={{
                            header: {
                              actions: " ",
                            },
                            body: {
                              emptyDataSourceMessage: (
                                <div
                                  style={{
                                    padding: "20px",
                                    textAlign: "center",
                                    color: "#666",
                                  }}
                                >
                                  <strong>{t("noFilesSelected")}</strong>
                                  <br />
                                  {t("dragDropMessage")}
                                </div>
                              ),
                            },
                          }}
                        >
                          {" "}
                        </MaterialTable>
                      )}
                    </FileDropZone>
                    {/* <div className={classes.dropZone}>
                      <FileDropZone
                        handleFilesSelected={handleCpoFilesSelected}
                        allowMultipleFiles={true}
                        cpoFileDropZone={true}
                        acceptedFileTypes={
                          ValidationConstants.ACCEPTED_CPO_FILE_TYPES_BLOCKS_WITH_EXCEL_TYPES
                        }
                        dropzoneDisabledBool={dropzoneDisabledBool}
                      />
                    </div> */}
                  </div>
                </Grid>
              )}

              <Dialog
                open={submitDialogOpen}
                onClose={handleDialogClose}
                aria-labelledby="dialog-title"
                aria-describedby="dialog-description"
              >
                <DialogTitle id="dialog-title">
                  {t("submitFilesQuestion")}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="dialog-description">
                    {t("fileListIs")}
                    <strong>{`Block ${blockData?.block.name} `}</strong>{" "}
                    {t("are")}
                    {dataToBeProcessed &&
                      (Array.isArray(dataToBeProcessed) ? (
                        dataToBeProcessed.map((item, index) => (
                          <>
                          <li key={index}>{item.file.name}{item.alreadyProcessed && <b> - WARNING - File has previously been processed.</b>}</li>
                          </>
                        ))
                      ) : (
                        <li>{dataToBeProcessed.file.name}</li>
                      ))}
                  </DialogContentText>
                </DialogContent>

                {(auth?.hasRealmRole(Roles.administrator) ||
                  auth?.hasRealmRole(Roles.cpoFileSubmitter)) && (
                  <DialogActions>
                    <Button onClick={() => handleDialogClose()} color="primary">
                      {"Cancel"}
                    </Button>
                    {auth?.hasRealmRole(Roles.administrator) && (
                      <Tooltip
                        title={
                          "Only files that have ALREADY BEEN PROCESSED will be uploaded straight to Azure file storage - unprocessed files will be ignored."
                        }
                        arrow={true}
                      >
                        <Button
                          onClick={() =>
                            handleSubmitCPOFilesToBeProcessed(true)
                          }
                          color="primary"
                          autoFocus
                        >
                          {"Upload Only"}
                        </Button>
                      </Tooltip>
                    )}
                    <Button
                      onClick={() => handleSubmitCPOFilesToBeProcessed(false)}
                      color="primary"
                      autoFocus
                    >
                      {"Submit"}
                    </Button>
                  </DialogActions>
                )}
              </Dialog>

              <Grid item xs={8} className={classes.middle}>
                <Paper className={classes.calendar}>
                  <FullCalendar
                    // key={blockData?.block?.name || 'calendar-loading'}
                    locales={allLocales}
                    locale={i18n.language}
                    initialDate={initialCalendarDate}
                    events={allCalendarEvents}
                    // eventSources={[
                    //   processedFilesForCalendar,
                    //   selectedDateForCalendar,
                    //   failedCheckDatesForCalendar,
                    // ]}
                    selectable={true}
                    select={function(info) {
                      const selectedDayForCalendar: CPOFileCalendarEntry[] = [];
                      setSelectedDateForCalendar(selectedDayForCalendar);
                      handleFilterCpoMonthData(info);
                    }}
                    showNonCurrentDates={false}
                    plugins={[dayGridPlugin, interactionPlugin]}
                    headerToolbar={{
                      left: "prev,next",
                      center: "title",
                      right: "monthRefreshButton",
                    }}
                    customButtons={{
                      monthRefreshButton: {
                        text: t("refreshCalendar"),
                        click() {
                          setMonthSelectedBool(true);
                          if (dateRangeSelected) {
                            handleMonthSelected(dateRangeSelected);
                          }
                        },
                      },
                    }}
                    buttonText={{
                      today: "Today",
                      month: "Month",
                      week: "Week",
                      day: "Day",
                    }}
                    datesSet={(dateRangeData: DatesSetArg) => {
                      setMonthSelectedBool(true);
                      console.log(dateRangeData);
                      setDateRangeSelected(dateRangeData);
                      handleMonthSelected(dateRangeData);
                    }}
                    initialView="dayGridMonth"
                    dayMaxEvents={false}
                    // dayMaxEventRows={4}
                    height={"100%"}
                    dayCellContent={(arg) => {
                      const tooltipContent = renderTooltipContent(arg.date);

                      if (!tooltipContent) {
                        return <span>{arg.dayNumberText}</span>;
                      }
                      const dayString = format(arg.date, "yyyy-MM-dd");
                      // const hasMissingFiles = processedFilesForCalendar
                      //   .filter((e) => e.start === dayString)
                      //   .some((e) => e.title.includes(t("missing")));

                      const dayEvents = processedFilesForCalendar.filter(
                        (e) => e.start === dayString,
                      );

                      // Check for missing and low confidence statuses
                      const hasMissingFiles = dayEvents.some((e) =>
                        e.title.includes(t("missing")),
                      );
                      // const hasLowConfidenceFiles = dayEvents.some((e) =>
                      //   e.color === "#63ceff"
                      // );

                      const dynamicBgColor = hasMissingFiles
                        ? "rgba(170, 170, 170, 0.3)"
                        : "rgba(76, 175, 80, 0.2)";

                      return (
                        <>
                          <Tooltip title={tooltipContent} arrow placement="top">
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                // right: 0, bottom: 0,
                                width: "100%",
                                height: "100%",
                                zIndex: 0,
                                cursor: "help",
                                backgroundColor: dynamicBgColor,
                              }}
                            />
                          </Tooltip>

                          <div
                            style={{
                              position: "relative",
                              zIndex: 1,
                              padding: "4px",
                              pointerEvents: "none",
                            }}
                          >
                            {arg.dayNumberText}
                          </div>
                        </>
                      );
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12} className={classes.right}>
                {monthCpoData && dateRangeSelected && monthSelectedBool && (
                  <>
                    {/* <Paper>
                  <Grid item xs={12} className={classes.infoDisplay}>
                    <Grid item className={classes.monthTitle}>
                      <Typography variant="h6">
                        {"Block " +
                          blockData?.block.name +
                          " CPO Processing - " +
                          dateRangeSelected?.start.toLocaleString("default", {
                            month: "long",
                          })}
                      </Typography>
                    </Grid>

                     <Typography variant="h4">
                      {"Summary of Month Data"}
                    </Typography>
                    <Typography variant="body1">
                      {
                        "Data from summary of processed files, and data from month's individual report"
                      }
                    </Typography>
                  </Grid>
                </Paper> */}

                    <Grid item xs={12} className={classes.rightTable}>
                      <MaterialTable
                        tableRef={processedDatesTableRef}
                        options={{
                          draggable: false,
                          selection: false,
                          padding: "default",
                          pageSizeOptions: [],
                          showFirstLastPageButtons: false,
                          pageSize: 6,
                          tableLayout: "auto",
                          detailPanelType: "single",
                          search: false,
                          rowStyle: (rowData) => {
                            let finalBackgroundColor = "rgb(55, 136, 216)";
                              // Colors.CPO_SUBMISSION_DATE_CHECKS_GOOD;

                            if (
                              rowData.assets &&
                              Array.isArray(rowData.assets)
                            ) {
                              let foundBad = false;
                              let foundNaN = false;

                              for (const asset of rowData.assets) {
                                if (asset) {

                                  const rawConf = asset.confidenceLevel as any;
                                  const confLevel =
                                    rawConf === "" || rawConf === null || rawConf === undefined
                                      ? NaN
                                      : Number(rawConf);

                                  if (!isNaN(confLevel) && confLevel < 0.7) {
                                    foundBad = true;
                                    break;
                                  } else if (isNaN(confLevel)) {
                                    foundNaN = true;
                                  }
                                }
                              }

                              if (foundBad) {
                                finalBackgroundColor =
                                  // Colors.CPO_SUBMISSION_DATE_CHECKS_BAD;
                                  "#63ceff";
                              } else if (foundNaN) {
                                finalBackgroundColor =
                                  "#63ceff";
                              }
                            }

                            return {
                              backgroundColor: finalBackgroundColor,
                            };
                          },
                        }}
                        title={
                          t("processedDatesTitle") +
                          monthCpoData.uniqueWeekNumbers
                        }
                        icons={TableIcons}
                        data={
                          (filteredMonthCpoDataBool
                            ? filteredMonthCpoData?.dates
                            : monthCpoData?.dates) || []
                        }
                        columns={[
                          {
                            title: t("dateProcessedDatesTable"),
                            field: "date",
                          },
                          {
                            title: t("totalBarrelLossProcessedDatesTable"),
                            field: "barrelLoss",
                            render: (rowData) => {
                              const assets = rowData.assets;

                              if (!assets || assets.length === 0) {
                                return "N/A";
                              }
                              const totalLoss = assets.reduce((acc, file) => {
                                const lossValue = Number(file.barrelLoss || 0);
                                return acc + lossValue;
                              }, 0);

                              console.log(totalLoss);

                              return <>{totalLoss.toFixed(2) + " mbod"}</>;
                            },
                          },
                          {
                            title: t("minConfidenceLevelProcessedDatesTable"),
                            field: "confidenceLevel",
                            sorting: false,
                            render: (rowData) => {
                              const assets = rowData.assets;
                              if (!assets || assets.length === 0) {
                                return "N/A";
                              }
                              // const confidenceLevels = assets.map(
                              //   (asset) => asset.confidenceLevel,
                              // );

                              // const validConfidenceLevels =
                              //   confidenceLevels.filter(
                              //     (value) => !Number.isNaN(value),
                              //   );

                              const validConfidenceLevels = assets
                                .map((asset) => {
                                  const rawConf = asset.confidenceLevel as any;
                                  if (rawConf === "" || rawConf === null || rawConf === undefined) {
                                    return NaN;
                                  }
                                  return Number(rawConf);
                                })
                                .filter((value) => !Number.isNaN(value));
                              if (validConfidenceLevels.length === 0) {
                                return "N/A";
                              }
                              const minimum = Math.min(
                                ...validConfidenceLevels,
                              );
                              return (
                                <>
                                  {(minimum * 100).toFixed(0)}
                                  {"%"}
                                </>
                              );
                            },
                          },
                        ]}
                        onRowClick={(event, rowData, togglePanel) => {
                          if (rowData) {
                            const selectedDayForCalendar: CPOFileCalendarEntry[] =
                              [];
                            let dateRowColor: string;
                            if (isNaN(rowData.files[0].barrelLossCheck)) {
                              dateRowColor =
                                Colors.CPO_SUBMISSION_DATE_SELECTED_CHECKS_NaN;
                            } else if (
                              rowData.files[0].barrelLossCheck > 10 ||
                              rowData.files[0].barrelLossCheck < -10
                            ) {
                              dateRowColor =
                                Colors.CPO_SUBMISSION_DATE_SELECTED_CHECKS_BAD;
                            } else {
                              dateRowColor =
                                Colors.CPO_SUBMISSION_DATE_SELECTED_CHECKS_GOOD;
                            }

                            selectedDayForCalendar.push({
                              title: ``,
                              start: rowData.date,
                              display: `background`,
                              color: dateRowColor,
                            });

                            setSelectedDateForCalendar(selectedDayForCalendar);
                          }

                          if (togglePanel) {
                            togglePanel();
                          }
                        }}
                        localization={{
                            header: {
                              actions: " ",
                            },
                            body: {
                              emptyDataSourceMessage: (
                                <div
                                  style={{
                                    padding: "20px",
                                    textAlign: "center",
                                    color: "#666",
                                  }}
                                >
                                  <strong>{t("noProcessedDates")}</strong>
                                  <br />
                                </div>
                              ),
                            },
                          }}
                        detailPanel={(rowData) => {
                          return (
                            <>
                              <Grid container className={classes.monthTitle}>
                                <Grid item xs={4}>
                                  {rowData.assets.map((asset) => (
                                    <>
                                      <div>
                                        <Typography variant={"body1"}>
                                          <b>
                                            {asset.assetName}
                                            {t("valuesProcessedDatesTable")}
                                          </b>
                                        </Typography>
                                      </div>
                                      <div>
                                        {auth?.hasRealmRole(
                                          Roles.cpoDeferralEditor,
                                        ) ||
                                        auth?.hasRealmRole(
                                          Roles.administrator,
                                        ) ? (
                                          <Tooltip
                                            title={
                                              "Click here for Block " +
                                              blockData.block.name +
                                              "'s CPO deferrals for date: " +
                                              rowData.date
                                            }
                                            arrow={true}
                                          >
                                            <Typography
                                              variant={"body2"}
                                              onClick={() =>
                                                handleDeferralDateClick(rowData)
                                              }
                                              style={{
                                                display: "inline-block",
                                                fontStyle: "italic",
                                                cursor: "pointer",
                                              }}
                                            >
                                              <u>
                                                {t("barrelLossProcessedDatesTable")}
                                                {asset.barrelLossTwoSigFig ?
                                                  (asset.barrelLossTwoSigFig) : ((asset.barrelLoss * 1).toFixed(2))
                                                }
                                                {" mbod"}
                                              </u>
                                            </Typography>
                                          </Tooltip>
                                        ) : (
                                          <Typography
                                            variant={"body2"}
                                            style={{
                                              display: "inline-block",
                                              fontStyle: "italic",
                                            }}
                                          >
                                            <u>
                                              {t("barrelLossProcessedDatesTable")}
                                               {asset.barrelLossTwoSigFig ?
                                                  (asset.barrelLossTwoSigFig) : ((asset.barrelLoss * 1).toFixed(2))
                                                }
                                              {" mbod"}
                                            </u>
                                          </Typography>
                                        )}
                                      </div>
                                      {asset.confidenceLevel && asset.confidenceLevel > 0 &&
                                      <div>
                                        {" "}
                                        <i>{t("confidenceLevelProcessedDatesTable")}</i>
                                        {Number.isNaN(asset.confidenceLevel)
                                          ? "N/A"
                                          : (
                                              asset.confidenceLevel * 100
                                            ).toFixed(0) + "%"}
                                      </div>
                        }
                                    </>
                                  ))}
                                </Grid>

                                <Grid item xs={4}>
                                  {rowData.unique_files.map((file) => (
                                    <div>
                                      {(file.fileName && azureUploadRecordMap[file.fileName]) ? (
                                        <>
                                          {file.fileName
                                            .toLowerCase()
                                            .endsWith(".pdf") ? (
                                            <Tooltip
                                              title={
                                                <>{t("clickHereToViewFile")}</>
                                              }
                                              arrow={true}
                                            >
                                              <Typography
                                                onClick={() =>
                                                  handleGetFileUrlClick(file)
                                                }
                                                variant={"body2"}
                                                style={{
                                                  padding: "2px 4px",
                                                  borderRadius: "4px",
                                                  display: "inline-block",
                                                  fontStyle: "italic",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                <u>{file.fileName}</u>
                                              </Typography>
                                            </Tooltip>
                                          ) : (
                                            <Tooltip
                                              title={
                                                // <>{t("cannotViewFileTooltip")}</>
                                                 <>{t("downloadFileTooltip")}</>
                                              }
                                              arrow={true}
                                            >
                                              <Typography
                                              onClick={() =>
                                                  handleGetFileUrlClick(file)
                                                }
                                                variant={"body2"}
                                                style={{
                                                  padding: "2px 4px",
                                                  borderRadius: "4px",
                                                  display: "inline-block",
                                                  fontStyle: "italic",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                <u>{file.fileName}</u>
                                              </Typography>
                                            </Tooltip>
                                          )}
                                          {file.isQueued && (
                                            <Tooltip
                                              title={
                                                <div
                                                  style={{
                                                    whiteSpace: "pre-line",
                                                    lineHeight: "1.5",
                                                  }}
                                                >
                                                  <strong>
                                                    Processing Queue Status of
                                                    file:
                                                  </strong>
                                                  <br />
                                                  • Done: File has successfully
                                                  processed
                                                  <br />
                                                  • Running: File is currently
                                                  processing
                                                  <br />
                                                  • Queued: File is currently
                                                  waiting in a queue
                                                  <br />
                                                  • Retry: File has failed /
                                                  processing is delayed, server
                                                  will retry
                                                  <br />• Failed: File has
                                                  failed processing
                                                </div>
                                              }
                                              arrow={true}
                                            >
                                              <Typography
                                                variant={"body2"}
                                                style={{
                                                  padding: "2px 4px",
                                                  borderRadius: "4px",
                                                  display: "inline-block",
                                                  fontStyle: "italic",
                                                }}
                                              >
                                                <i>
                                                  - Processing Queue Status:{" "}
                                                  <b>{file.queueStatus}</b>
                                                </i>
                                              </Typography>
                                            </Tooltip>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <Tooltip
                                            title={
                                              "File is not available to view"
                                            }
                                            arrow={true}
                                          >
                                            <Typography
                                              variant={"body2"}
                                              style={{
                                                padding: "2px 4px",
                                                borderRadius: "4px",
                                                display: "inline-block",
                                                fontStyle: "italic",
                                              }}
                                            >
                                              {file.fileName}
                                            </Typography>
                                          </Tooltip>
                                          {file.isQueued && (
                                            <Tooltip
                                              title={
                                                <div
                                                  style={{
                                                    whiteSpace: "pre-line",
                                                    lineHeight: "1.5",
                                                  }}
                                                >
                                                  <strong>
                                                    Processing Queue Status of
                                                    file:
                                                  </strong>
                                                  <br />
                                                  • Done: File has successfully
                                                  processed
                                                  <br />
                                                  • Running: File is currently
                                                  processing
                                                  <br />
                                                  • Queued: File is currently
                                                  waiting in a queue
                                                  <br />
                                                  • Retry: File has failed /
                                                  processing is delayed, server
                                                  will retry
                                                  <br />• Failed: File has
                                                  failed processing
                                                </div>
                                              }
                                              arrow={true}
                                            >
                                              <Typography
                                                variant={"body2"}
                                                style={{
                                                  padding: "2px 4px",
                                                  borderRadius: "4px",
                                                  display: "inline-block",
                                                  fontStyle: "italic",
                                                }}
                                              >
                                                <i>
                                                  Processing Queue Status:{" "}
                                                  {file.queueStatus}
                                                </i>
                                              </Typography>
                                            </Tooltip>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </Grid>

                                <Grid item xs={4}>
                                  {blockData.block.name === "15" ? (
                                    <div>
                                      {rowData.unique_files.length < 5 ? (
                                        <Typography
                                          variant={"body2"}
                                          style={{
                                            backgroundColor: "red",
                                            color: "white",
                                            padding: "2px 4px",
                                            borderRadius: "4px",
                                            display: "inline-block",
                                          }}
                                        >
                                          <i>
                                            {"Only "}
                                            {rowData.unique_files.length}
                                            {
                                              " files processed. Please check what report is missing: "
                                            }
                                            <b>{"4 Daily Reports "}</b>
                                            {"per asset and "}
                                            <b>{"1 Summary Report "}</b>
                                            {"is needed for each day."}
                                          </i>
                                        </Typography>
                                      ) : (
                                        <Typography variant={"body2"}>
                                          <i>
                                            {rowData.unique_files.length}
                                            {
                                              " files processed. Please check if the "
                                            }
                                            <b>{"4 Daily Reports "}</b>
                                            {"per asset and "}
                                            <b>{"1 Summary Report "}</b>
                                            {"has been correctly processed."}
                                          </i>
                                        </Typography>
                                      )}
                                    </div>
                                  ) : null}
                                </Grid>
                              </Grid>
                            </>
                          );
                        }}
                      ></MaterialTable>
                      {/* {(auth?.hasRealmRole(Roles.administrator) &&
                    <Paper className={classes.filesListPaper}>
                                        <Typography variant="h6" className={classes.monthTitle}>
                    {"Processed and Uploaded Files for Block " +
                      blockData?.block.name +
                      " - " +
                      dateRangeSelected?.start.toLocaleString("default", {
                        month: "long",
                      }) +
                      " " +
                      dateRangeSelected?.start.toLocaleString("default", {
                        year: "numeric",
                      })}
                  </Typography>
                    <List className={classes.filesList}>
                      {monthCpoData?.dates
                      ?.flatMap((dateObj) => dateObj.unique_files || [])
                      ?.map((file, index) => (
                      <ListItem key={`${file.fileName}-${index}`}>
                                      {azureUploadRecordMap[file.fileName] ? (
                                      <Tooltip
                                        title={"Click here to view this file"}
                                        arrow={true}
                                      >
                                        <Typography
                                          onClick={() =>
                                            handleGetFileUrlClick(file)
                                          }
                                          variant={"body2"}
                                          style={{
                                            padding: "2px 4px",
                                            borderRadius: "4px",
                                            display: "inline-block",
                                            fontStyle: "italic",
                                            cursor: "pointer",
                                          }}
                                        >
                                          <u>{file.fileName}</u>
                                        </Typography>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip
                                        title={"File is not available to view"}
                                        arrow={true}
                                      >
                                        <Typography
                                          variant={"body2"}
                                          style={{
                                            padding: "2px 4px",
                                            borderRadius: "4px",
                                            display: "inline-block",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          {file.fileName}
                                        </Typography>
                                      </Tooltip>
                                    )}
                                      </ListItem>
                                    ))}
                        </List>
                    </Paper>
                    )} */}
                    </Grid>
                                        <Grid item xs={12} className={classes.rightTable}
                    >

                <MaterialTable
                        // tableRef={processedDatesTableRef}
                        options={{
                          draggable: false,
                          selection: false,
                          padding: "default",
                          pageSizeOptions: [],
                          showFirstLastPageButtons: false,
                          pageSize: 6,
                          tableLayout: "auto",
                          detailPanelType: "single",
                          search: false,
                          rowStyle: (rowData) => {
                            const finalBackgroundColor = "rgb(55, 136, 216)";
                            return {
                              backgroundColor: finalBackgroundColor,
                            };
                          },
                        }}
                        title={
                          t("filesForYearForBlock") + blockData.block.name + " - " +                         dateRangeSelected?.start.toLocaleString("default", {
                          year: "numeric",
                        })
                        }
                        icons={TableIcons}
                        data={
                          (
                            yearCpoFileNames ?
                            yearCpoFileNames?.files : [])
                        }
                        columns={[
                          {
                            title: t("fileNameYearFiles"),
                            field: "filename",
                            render: (rowData) => {
                              const fileName = rowData.fileName;

                              if (!fileName) { return null; }

                              return fileName
                              .toLowerCase()
                              .endsWith(".pdf") ? (
                              <Tooltip
                                              title={
                                                <>{t("clickHereToViewFile")}</>
                                              }
                                              arrow={true}
                                            >
                                              <Typography
                                                onClick={() =>
                                                  handleGetFileUrlClick(rowData)
                                                }
                                                variant={"body2"}
                                                style={{
                                                  padding: "2px 4px",
                                                  borderRadius: "4px",
                                                  display: "inline-block",
                                                  fontStyle: "italic",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                <u>{fileName}</u>
                                              </Typography>
                                            </Tooltip>
                              ) : (
                                        <Tooltip
                                              title={
                                                // <>{t("cannotViewFileTooltip")}</>
                                                 <>{t("downloadFileTooltip")}</>
                                              }
                                              arrow={true}
                                            >
                                              <Typography
                                              onClick={() =>
                                                  handleGetFileUrlClick(rowData)
                                                }
                                                variant={"body2"}
                                                style={{
                                                  padding: "2px 4px",
                                                  borderRadius: "4px",
                                                  display: "inline-block",
                                                  fontStyle: "italic",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                <u>{fileName}</u>
                                              </Typography>
                                            </Tooltip>
                              );
                            },
                          },
                          {
                            title: t("documentType"),
                            field: "document_type",
                            render: (rowData) => {
                              let documentType;
                              if (rowData.documentType && rowData.documentType !== null && rowData.documentType !== "") {
                              if (rowData.documentType === "monthly_production") {
                                documentType = "Monthly Production";
                              } else if (rowData.documentType === "contracts") {
                                documentType = "Contract";
                              } else if (rowData.documentType === "vulnerabilities") {
                                documentType = "Vulnerabilities";
                              }
                              } else {
                                documentType = "N/A";
                              }

                              return<>{documentType}</>;
                            },
                          },
                        ]}
                        // onRowClick={(event, rowData) => {
                        //   if (rowData) {
                        //   handleGetFileUrlClick(rowData)
                        //   }
                        // }}
                        localization={{
                            header: {
                              actions: " ",
                            },
                            body: {
                              emptyDataSourceMessage: (
                                <div
                                  style={{
                                    padding: "20px",
                                    textAlign: "center",
                                    color: "#666",
                                  }}
                                >
                                  <strong>{t("noProcessedYearFiles")}</strong>
                                  <br />
                                </div>
                              ),
                            },
                          }}

                      ></MaterialTable>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </div>
        ) : blockData?.block.name &&
          enabledCpoSubmissionBlocks &&
          !enabledCpoSubmissionBlocks.includes(blockData?.block.name) &&
          fetchMonthDataError === false ? (
          <Grid container>
            <Grid item xs={4} className={classes.left}>
              <Paper className={classes.calendar}>
                File Submission is currently disabled for this block. Please
                contact OGC Greenworld if you believe this to be a mistake.
              </Paper>
            </Grid>
          </Grid>
        ) : (
          fetchMonthDataError === true && (
            <Grid container>
              <Grid item xs={4} className={classes.left}>
                <Paper className={classes.calendar}>
                  Unable to connect to the CPO database. Please check if it is
                  available.
                </Paper>
              </Grid>
            </Grid>
          )
        )}
        <Snackbar
          open={submitPopUpOpen}
          autoHideDuration={TimeConstants.SNACKBAR_ALERT_DURATION}
          onClose={handleSubmitPopUpClose}
        >
          <Alert onClose={handleSubmitPopUpClose} severity="success">
            The files were submitted. Please wait while they are processed.
          </Alert>
        </Snackbar>

        <Dialog
          open={submitResponsePopUpOpen}
          onClose={handleSubmitResponsePopUpClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Processing Results</DialogTitle>
          <DialogContent>
            {justProcessedFiles.some((f) => f.status === "warning") && (
              <Box mb={2}>
                <Alert severity="warning" variant="outlined">
                  <AlertTitle>Processing in Progress</AlertTitle>
                  The connection timed out before the server could confirm
                  completion.
                  <strong>
                    {" "}
                    Please check the calendar and processed dates table in 5 -
                    10 minutes{" "}
                  </strong>{" "}
                  before attempting to upload these files again.
                </Alert>
              </Box>
            )}
            <TableContainer component={Paper} variant="outlined">
              <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="caption">Total Files</Typography>
                    <Typography variant="h6">
                      {uploadSummary.totalFiles}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption">Upload</Typography>
                    <Typography variant="h6">
                      {uploadSummary.uploadTime}s
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption">Processing</Typography>
                    <Typography variant="h6">
                      {uploadSummary.processingTime}s
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption">Total</Typography>
                    <Typography variant="h6">
                      {uploadSummary.totalTime}s
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell align="center">Data Processing Status</TableCell>
                    <TableCell align="center">Azure Upload Status</TableCell>
                    <TableCell align="center">Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {justProcessedFiles.map((file, index) => (
                    <TableRow key={index}>
                      <TableCell>{file.filename}</TableCell>
                      <TableCell>
                        {FileUtils.formatFileSize(file.fileSize || 0)}
                      </TableCell>
                      <TableCell align="center">
                        {/* If ai_status is "done" or blank (""), then look at file.status. Otherwise, "Data Processing Status" section should show ai_status */}
                        {file.ai_status === "done" || file.ai_status === "" ? (
                          file.status === "success" ? (
                            <>
                              <CheckCircleIcon style={{ color: "green" }} />
                              <Chip
                                label="Processed"
                                size="small"
                                variant="outlined"
                              />
                            </>
                          ) : file.status === "warning" ? (
                            <WarningIcon style={{ color: "orange" }} />
                          ) : file.status === "skipped" ? (
                            <>
                              <WarningIcon style={{ color: "yellow" }} />
                              <Chip
                                label="Processing Skipped - Upload Only Selected"
                                size="small"
                                variant="outlined"
                              />
                            </>
                          ) : (
                            <>
                              <ErrorIcon style={{ color: "red" }} />
                              <Chip
                                label="Processing Failed"
                                size="small"
                                variant="outlined"
                              />
                            </>
                          )
                        ) : (
                          (file.ai_status === "queued" ||
                            file.ai_status === "retry" ||
                            file.ai_status === "running") && (
                            <>
                              <MoreHorizIcon style={{ color: "#f39c12" }} />
                              <Chip
                                label="Queued"
                                size="small"
                                variant="outlined"
                              />
                            </>
                          )
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {/* {blockData?.block.name === "18" ||
                        blockData?.block.name === "31" ||
                        blockData?.block.name === "2_05" ||
                        blockData?.block.name === "3_05" ||
                        blockData?.block.name === "FS_FST" ||
                        blockData?.block.name === "15" ||
                        blockData?.block.name === "15_06" ||
                        blockData?.block.name === "17" ||
                        blockData?.block.name === "17_06" ||
                        blockData?.block.name === "32" ||
                        blockData?.block.name === "14" ||
                        blockData?.block.name === "0" ? ( */}
                        {file.upload_status === "success" ? (
                          <>
                            <CheckCircleIcon style={{ color: "green" }} />
                            <Chip
                              label="Uploaded"
                              size="small"
                              variant="outlined"
                            />
                          </>
                        ) : file.upload_status === "skipped" ? (
                          <Chip
                            label="N/A"
                            size="small"
                            variant="outlined"
                            style={{ color: "gray" }}
                          />
                        ) : (
                          <Chip
                            label="Upload Failed"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {/* // ) : (
                        //   <Chip
                        //     label="Skipped - Feature is Work in Progress"
                        //     size="small"
                        //     variant="outlined"
                        //     style={{ color: "gray" }}
                        //   />
                        // )} */}
                      </TableCell>
                      <TableCell>
                        {file.message ? file.message : file.error}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                {
                  justProcessedFiles.filter((f) => f.status === "success")
                    .length
                }
                {" file(s) successfully processed."}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            {auth?.hasRealmRole(Roles.administrator) && (
              <Button
                onClick={handleSummaryReportDownloadClick}
                variant="outlined"
                startIcon={<ExcelDownloadIcon />}
              >
                Download Processing Report
              </Button>
            )}
            <Button
              onClick={handleSubmitResponsePopUpClose}
              color="primary"
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
  );
};
